import React, { useState, useEffect, useCallback } from 'react';
import { getAllProperties, updatePropertyStatus } from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';

// MUI Components
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Stack,
  Avatar,
  MobileStepper,
  useTheme,
} from '@mui/material';

// MUI Icons
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import VideocamIcon from '@mui/icons-material/Videocam';

// Property status chip styles
const getStatusChipStyle = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return { bg: '#E1FCEF', color: '#10B981' };
    case 'INACTIVE':
      return { bg: '#F3F4F6', color: '#6B7280' };
    default:
      return { bg: '#F3F4F6', color: '#6B7280' };
  }
};

// Property Types based on schema
const PROPERTY_TYPES = ["Flat", "PG", "Preoccupied Flats"];

// Property Statuses based on schema
const PROPERTY_STATUSES = ["ACTIVE", "INACTIVE"];

const PropertiesManagement: React.FC = () => {
  const { admin } = useAuth();
  const theme = useTheme();
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalProperties, setTotalProperties] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table');
  const [statusChangeLoading, setStatusChangeLoading] = useState<{[key: string]: boolean}>({});
  const [detailTab, setDetailTab] = useState<number>(0);
  const [activeMediaStep, setActiveMediaStep] = useState(0);

  // Create a debounced version of search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      setPage(0);
      fetchProperties(searchValue);
    }, 500),
    [filterType, filterStatus]
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Fetch properties
  const fetchProperties = async (search = searchTerm) => {
    try {
      setLoading(true);
      const response = await getAllProperties({
        status: filterStatus,
        type: filterType,
        search,
        page: page + 1,
        limit: rowsPerPage,
      });
      
      setProperties(response.properties);
      setTotalProperties(response.pagination.totalProperties);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchProperties();
  }, [page, rowsPerPage, filterStatus, filterType]);

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open view dialog
  const handleOpenViewDialog = (property: any) => {
    setSelectedProperty(property);
    setViewDialogOpen(true);
  };

  // Close view dialog
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedProperty(null);
  };

  // Handle status change with switch toggle
  const handleStatusChange = async (propertyId: string, newStatus: boolean) => {
    try {
      setStatusChangeLoading(prev => ({ ...prev, [propertyId]: true }));
      const statusValue = newStatus ? 'ACTIVE' : 'INACTIVE';
      
      await updatePropertyStatus(propertyId, statusValue);
      
      // Update local state to reflect the change
      setProperties(properties.map(p => 
        p._id === propertyId 
          ? { ...p, status: statusValue } 
          : p
      ));
      
      toast.success(`Property ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update property status');
    } finally {
      setStatusChangeLoading(prev => ({ ...prev, [propertyId]: false }));
    }
  };

  // Handle detail tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDetailTab(newValue);
  };

  // Toggle view mode
  const toggleViewMode = () => {
    setViewMode(viewMode === 'table' ? 'card' : 'table');
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Format amenities for display
  const formatAmenities = (amenities: string[]) => {
    if (!amenities || !Array.isArray(amenities) || amenities.length === 0) {
      return "No amenities listed";
    }
    
    return amenities.join(", ");
  };

  // Photos Tab - Update with carousel and videos support
  const handleNextMedia = () => {
    setActiveMediaStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBackMedia = () => {
    setActiveMediaStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Function to get all media (images and videos)
  const getPropertyMedia = (property: any) => {
    const images = property?.images || [];
    const videos = property?.videos || [];
    
    const allMedia = [
      ...images.map((image: string) => ({ type: 'image', url: image })),
      ...videos.map((video: string) => ({ type: 'video', url: video }))
    ];
    
    return allMedia;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#0F0F2D' }}>
          Properties Management
        </Typography>
        
        <Button 
          variant="outlined" 
          onClick={toggleViewMode}
          startIcon={viewMode === 'table' ? <GridViewIcon /> : <TableViewIcon />}
        >
          {viewMode === 'table' ? 'Card View' : 'Table View'}
        </Button>
      </Box>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              placeholder="Search properties..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {PROPERTY_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel id="type-filter-label">Property Type</InputLabel>
              <Select
                labelId="type-filter-label"
                value={filterType}
                label="Property Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                {PROPERTY_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress size={60} sx={{ color: '#7065F0' }} />
        </Box>
      )}

      {/* Empty State */}
      {!loading && properties.length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <HomeIcon sx={{ fontSize: 60, color: '#9CA3AF', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
            No properties found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Paper>
      )}

      {/* Card View */}
      {!loading && properties.length > 0 && viewMode === 'card' && (
        <Grid container spacing={3}>
          {properties.map((property) => {
            const statusStyle = getStatusChipStyle(property.status);
            const isLoading = statusChangeLoading[property._id] || false;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={property._id}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 2,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  } 
                }}>
                  <CardMedia
                    component="img"
                    height="180"
                    image={property.images?.[0] || 'https://via.placeholder.com/300x180?text=No+Image'}
                    alt={property.title}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        {property.propertyName || `${property.propertyType} in ${property.address}`}
                      </Typography>
                      <Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={property.status === 'ACTIVE'}
                              onChange={(e) => handleStatusChange(property._id, e.target.checked)}
                              disabled={isLoading}
                              size="small"
                            />
                          }
                          label={isLoading ? <CircularProgress size={16} /> : property.status}
                          sx={{ mr: 0 }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocationOnIcon fontSize="small" sx={{ color: '#9CA3AF', mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        {property.address}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {property.propertyType} • {property.bhk || (property.bedrooms ? `${property.bedrooms} BHK` : '')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="#0F0F2D">
                      ₹{property?.seo?.price || '(Contact for price)'}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /month
                      </Typography>
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleOpenViewDialog(property)}
                      fullWidth
                      variant="outlined"
                      sx={{ borderColor: '#7065F0', color: '#7065F0' }}
                    >
                      View Details
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Table View */}
      {!loading && properties.length > 0 && viewMode === 'table' && (
        <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#F7F7FD' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Rent</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.map((property) => {
                const isLoading = statusChangeLoading[property._id] || false;
                
                return (
                  <TableRow key={property._id} hover>
                    <TableCell>{property?.propertyName || `${property.propertyType} in ${property.address}`}</TableCell>
                    <TableCell>{property?.address}</TableCell>
                    <TableCell>{property?.propertyType}</TableCell>
                    <TableCell>₹{property?.seo?.price?.toLocaleString('en-IN') || '(Contact)'}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={property.status === 'ACTIVE'}
                            onChange={(e) => handleStatusChange(property._id, e.target.checked)}
                            disabled={isLoading}
                            size="small"
                          />
                        }
                        label={isLoading ? <CircularProgress size={16} /> : property.status}
                        sx={{ mr: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenViewDialog(property)}
                            sx={{ color: '#7065F0' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalProperties}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* View Property Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {selectedProperty?.propertyName || `${selectedProperty?.propertyType} in ${selectedProperty?.address}`}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={selectedProperty?.status === 'ACTIVE'}
                onChange={(e) => {
                  handleStatusChange(selectedProperty?._id, e.target.checked);
                  setSelectedProperty((prev:any) => ({
                    ...prev,
                    status: e.target.checked ? 'ACTIVE' : 'INACTIVE'
                  }));
                }}
                disabled={statusChangeLoading[selectedProperty?._id]}
                color="primary"
              />
            }
            label={
              statusChangeLoading[selectedProperty?._id] ? 
                <CircularProgress size={16} sx={{ ml: 1 }} /> : 
                (selectedProperty?.status === 'ACTIVE' ? "Active" : "Inactive")
            }
          />
        </DialogTitle>
        <DialogContent>
          {selectedProperty && (
            <>
              <Tabs value={detailTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                <Tab label="Overview" />
                <Tab label="Details" />
                <Tab label="Photos" />
                <Tab label="Owner Info" />
              </Tabs>
              
              {/* Overview Tab */}
              {detailTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <img 
                      src={selectedProperty.images?.[0] || 'https://via.placeholder.com/600x400?text=No+Image'} 
                      alt={selectedProperty.propertyName}
                      style={{ width: '100%', borderRadius: 8, marginBottom: 16 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocationOnIcon sx={{ color: '#9CA3AF', mr: 0.5 }} />
                      <Typography variant="body1">
                        {selectedProperty.address}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={600} color="#0F0F2D" sx={{ mb: 2 }}>
                      ₹{selectedProperty.monthlyRent?.toLocaleString('en-IN') || 'Contact for pricing'}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /month
                      </Typography>
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedProperty.description || 'No description available'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Property Highlights
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <HomeIcon sx={{ color: '#7065F0', mr: 1 }} />
                            <Typography variant="body1">
                              {selectedProperty.propertyType}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <BedIcon sx={{ color: '#7065F0', mr: 1 }} />
                            <Typography variant="body1">
                              {selectedProperty.bhk || selectedProperty.bedrooms || 'N/A'} BHK
                            </Typography>
                          </Box>
                        </Grid>
                        {selectedProperty.bathrooms && (
                          <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <BathtubIcon sx={{ color: '#7065F0', mr: 1 }} />
                              <Typography variant="body1">
                                {selectedProperty.bathrooms} Bathroom(s)
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {selectedProperty.furnished && (
                          <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <CheckCircleIcon sx={{ color: '#7065F0', mr: 1 }} />
                              <Typography variant="body1">
                                {selectedProperty.furnished}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CalendarTodayIcon sx={{ color: '#7065F0', mr: 1 }} />
                            <Typography variant="body1">
                              Available from: {formatDate(selectedProperty.dateAvailable)}
                            </Typography>
                          </Box>
                        </Grid>
                        {selectedProperty.securityDeposit && (
                          <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <LocalAtmIcon sx={{ color: '#7065F0', mr: 1 }} />
                              <Typography variant="body1">
                                Security: ₹{selectedProperty.securityDeposit.toLocaleString('en-IN')}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Card>
                    
                    {selectedProperty.amenities && selectedProperty.amenities.length > 0 && (
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Amenities
                        </Typography>
                        <Grid container spacing={1}>
                          {selectedProperty.amenities.map((amenity: string, index: number) => (
                            <Grid item xs={6} key={index}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CheckCircleIcon sx={{ color: '#10B981', mr: 1, fontSize: '18px' }} />
                                <Typography variant="body2">{amenity}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Card>
                    )}
                  </Grid>
                </Grid>
              )}
              
              {/* Details Tab */}
              {detailTab === 1 && (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Property Details
                        </Typography>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Property Type</Typography>
                            <Typography variant="body1">{selectedProperty.propertyType}</Typography>
                          </Box>
                          <Divider />
                          {selectedProperty.propertyType === "PG" ? (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">PG Type</Typography>
                                <Typography variant="body1">{selectedProperty.pgType}</Typography>
                              </Box>
                              <Divider />
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Food Option</Typography>
                                <Typography variant="body1">{selectedProperty.foodOption}</Typography>
                              </Box>
                            </>
                          ) : (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">BHK</Typography>
                                <Typography variant="body1">{selectedProperty.bhk || selectedProperty.bedrooms || 'N/A'}</Typography>
                              </Box>
                              <Divider />
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Bathrooms</Typography>
                                <Typography variant="body1">{selectedProperty.bathrooms || 'N/A'}</Typography>
                              </Box>
                            </>
                          )}
                          <Divider />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Furnished Status</Typography>
                            <Typography variant="body1">{selectedProperty.furnished || 'N/A'}</Typography>
                          </Box>
                          <Divider />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Available From</Typography>
                            <Typography variant="body1">{formatDate(selectedProperty.dateAvailable)}</Typography>
                          </Box>
                          <Divider />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Minimum Lock-in Period</Typography>
                            <Typography variant="body1">{selectedProperty.minimumLockInPeriod || 'N/A'}</Typography>
                          </Box>
                        </Stack>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Pricing Details
                        </Typography>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Monthly Rent</Typography>
                            <Typography variant="body1" fontWeight={600}>₹{selectedProperty.monthlyRent?.toLocaleString('en-IN') || 'Contact for pricing'}</Typography>
                          </Box>
                          <Divider />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Security Deposit</Typography>
                            <Typography variant="body1">₹{selectedProperty.securityDeposit?.toLocaleString('en-IN') || 'N/A'}</Typography>
                          </Box>
                        </Stack>
                        
                        {selectedProperty.propertyType === "PG" && selectedProperty.pgRooms && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>PG Room Options</Typography>
                            {selectedProperty.pgRooms.map((room: any, index: number) => (
                              <Box key={index} sx={{ mb: 2, p: 1, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                                <Typography variant="body2" fontWeight={600}>{room.type} Room</Typography>
                                <Typography variant="body2">₹{room.monthlyRent?.toLocaleString('en-IN')}/month</Typography>
                                <Typography variant="body2">Security: ₹{room.securityDeposit?.toLocaleString('en-IN')}</Typography>
                                <Typography variant="body2">{room.totalRooms} rooms available</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Card>
                      
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Address
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>{selectedProperty.flatHouseNo}</Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>{selectedProperty.address}</Typography>
                        {selectedProperty.city && (
                          <Typography variant="body1" sx={{ mb: 1 }}>{selectedProperty.city}</Typography>
                        )}
                        {selectedProperty.zipCode && (
                          <Typography variant="body1">Pin: {selectedProperty.zipCode}</Typography>
                        )}
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* Photos Tab */}
              {detailTab === 2 && (
                <Box sx={{ maxWidth: '100%', flexGrow: 1, position: 'relative', mb: 4 }}>
                  {(() => {
                    const allMedia = getPropertyMedia(selectedProperty);
                    
                    if (!allMedia || allMedia.length === 0) {
                      return (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                          <Typography variant="body1" color="text.secondary">No images or videos available</Typography>
                        </Box>
                      );
                    }
                    
                    const currentMedia = allMedia[activeMediaStep];
                    
                    return (
                      <>
                        <Box 
                          sx={{ 
                            height: 450, 
                            width: '100%', 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            backgroundColor: '#f8f8f8',
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: 'relative',
                            mb: 2
                          }}
                        >
                          {currentMedia.type === 'image' ? (
                            <Box
                              component="img"
                              src={currentMedia.url}
                              alt={`Property image ${activeMediaStep + 1}`}
                              sx={{
                                height: '100%',
                                maxWidth: '100%',
                                objectFit: 'contain',
                              }}
                            />
                          ) : (
                            <Box
                              component="video"
                              src={currentMedia.url}
                              controls
                              sx={{
                                height: '100%',
                                maxWidth: '100%',
                              }}
                            />
                          )}
                          
                          {currentMedia.type === 'video' && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 16,
                                left: 16,
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <VideocamIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="caption">Video</Typography>
                            </Box>
                          )}
                        </Box>
                        
                        <MobileStepper
                          variant="dots"
                          steps={allMedia.length}
                          position="static"
                          activeStep={activeMediaStep}
                          sx={{ 
                            maxWidth: '100%', 
                            flexGrow: 1,
                            "& .MuiMobileStepper-dot": {
                              backgroundColor: "#e0e0e0",
                            },
                            "& .MuiMobileStepper-dotActive": {
                              backgroundColor: "#7065F0",
                            }
                          }}
                          nextButton={
                            <Button
                              size="small"
                              onClick={handleNextMedia}
                              disabled={activeMediaStep === allMedia.length - 1}
                              sx={{ color: '#7065F0' }}
                            >
                              Next
                              {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                            </Button>
                          }
                          backButton={
                            <Button 
                              size="small" 
                              onClick={handleBackMedia} 
                              disabled={activeMediaStep === 0}
                              sx={{ color: '#7065F0' }}
                            >
                              {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                              Back
                            </Button>
                          }
                        />
                        
                        {/* Thumbnail Gallery */}
                        <Box sx={{ display: 'flex', overflowX: 'auto', gap: 1, mt: 2, pb: 1 }}>
                          {allMedia.map((media: any, index: number) => (
                            <Box
                              key={index}
                              onClick={() => setActiveMediaStep(index)}
                              sx={{
                                width: 80,
                                height: 60,
                                borderRadius: 1,
                                overflow: 'hidden',
                                flexShrink: 0,
                                cursor: 'pointer',
                                border: index === activeMediaStep ? '2px solid #7065F0' : '2px solid transparent',
                                position: 'relative',
                              }}
                            >
                              {media.type === 'image' ? (
                                <Box
                                  component="img"
                                  src={media.url}
                                  alt={`Thumbnail ${index + 1}`}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#000',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <VideocamIcon sx={{ color: 'white' }} />
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              )}
              
              {/* Owner Info Tab */}
              {detailTab === 3 && (
                <Box>
                  {selectedProperty.owner ? (
                    <Card variant="outlined" sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Avatar sx={{ bgcolor: '#7065F0', mr: 2 }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6">{selectedProperty.owner.name}</Typography>
                          <Typography variant="body2" color="text.secondary">Property Owner</Typography>
                        </Box>
                      </Box>
                      
                      <List>
                        <ListItem>
                          <ListItemIcon>
                            <EmailIcon />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Email" 
                            secondary={selectedProperty.owner.email} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <PhoneIcon />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Phone" 
                            secondary={selectedProperty.owner.phone || 'Not provided'} 
                          />
                        </ListItem>
                      </List>
                    </Card>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                      <Typography variant="body1" color="text.secondary">Owner information not available</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// MUI Icons
const GridViewIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"></rect>
      <rect x="14" y="3" width="7" height="7"></rect>
      <rect x="14" y="14" width="7" height="7"></rect>
      <rect x="3" y="14" width="7" height="7"></rect>
    </svg>
  );
};

const TableViewIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
  );
};

export default PropertiesManagement; 